import { CarryForwardType } from '../enums';
import { Lock, VisitInterface } from '../types';
import {
  acquireLock, fetchLock, LockRequestInterface,
} from '../api/esourceService';
import { getAuth } from './firebaseUtil';

export interface LockRequestParams {
  procedureId: string,
  studyId: string,
  subjectId?: string,
  visitId?: string,
}

/**
 * Safely execute a lock operation, handling errors gracefully.
 * If the lock request fails, attempts to parse the error as a Lock object.
 * Falls back to a default lock message if parsing fails.
 */
async function executeLockRequest(
  lockFunction: (lockRequest: LockRequestInterface) => Promise<Lock>,
  params: LockRequestParams,
  lock?: Lock,
): Promise<Lock> {
  const {
    procedureId, studyId, subjectId, visitId,
  } = params;
  try {
    return await lockFunction({
      subjectId,
      studyId,
      procedureId,
      visitId,
      lock,
    });
  } catch (error: any) {
    try {
      return JSON.parse(error.message as string) as Lock;
    } catch {
      // If there's a redis issue, keep the procedures unlocked
      return { lockMessage: 'Could not fetch lock' };
    }
  }
}

export interface FetchOrAcquireLockProps extends LockRequestParams {
  procedureReadOnly: boolean,
}

/**
 * Fetch or acquire the procedure lock depending on if this procedure is read only.
 * - Read-only procedures: just fetch the existing lock
 * - Editable procedures: attempt to acquire a new lock for the current user
 */
export async function fetchOrAcquireLock({
  procedureId, procedureReadOnly, studyId, subjectId, visitId,
}: FetchOrAcquireLockProps): Promise<Lock> {
  const params: LockRequestParams = {
    procedureId, studyId, subjectId, visitId,
  };

  if (procedureReadOnly) {
    return executeLockRequest(fetchLock, params);
  }

  const { currentUser } = getAuth();
  const newLock: Lock = {
    userId: currentUser?.uid,
    lockMessage: 'Locking procedure',
  };
  return executeLockRequest(acquireLock, params, newLock);
}

/**
 * Checks if a procedure is locked by another user.
 */
export function isLockedByAnotherUser(lock: Lock | null): boolean {
  const currentUserId = getAuth().currentUser?.uid;
  const lockUserId = lock?.userId;
  return Boolean(lockUserId && lockUserId !== currentUserId);
}

/**
 * Updates the procedure's locked state in the visit config and returns the lock holder's name
 * if the procedure is locked by another user.
 *
 * Note: This function mutates visitConfig.procedures for performance reasons,
 * as creating a deep copy on every lock check would be expensive.
 *
 * @returns The lock holder's username if locked by another user, empty string otherwise
 */
export function updateProcedureLockStatus(
  lock: Lock | null,
  visitConfig: VisitInterface,
  procedureId: string,
): string {
  const procedureIndex = visitConfig.procedures.findIndex((p) => p.procedureId === procedureId);
  if (procedureIndex === -1) {
    return '';
  }

  const lockedByOther = isLockedByAnotherUser(lock);
  // eslint-disable-next-line no-param-reassign
  visitConfig.procedures[procedureIndex].procedureLocked = lockedByOther;

  return lockedByOther ? lock?.userName ?? '' : '';
}

/**
 * Determines the visitId to use for locking operations.
 * Permanent carry-forward procedures are visit-independent and use a global lock (no visitId),
 * while other procedures are locked per-visit.
 */
export function getVisitIdForLock(
  carryForwardType: CarryForwardType | undefined,
  visitId: string | undefined,
): string | undefined {
  const isPermanentCarryForward = carryForwardType === CarryForwardType.PERMANENT;
  return isPermanentCarryForward ? undefined : visitId;
}

/**
 * Checks if the current user owns the given lock.
 */
export function currentUserOwnsLock(lock: Lock | undefined): boolean {
  const currentUserId = getAuth().currentUser?.uid;
  const lockUserId = lock?.userId;
  return Boolean(lockUserId && lockUserId === currentUserId);
}
